// Profile Manager — version-aware localStorage layer (P1 of Kill Sheet Redev).
//
// @deprecated RC-1 — Superseded by the RigSentinel Context Engine.
//   The canonical engineering data now lives on:
//
//     • Well Profile      (`rsc.wells.v1`,      lib/well/storage.js)
//     • Rig Data Sheet    (`rsc.rigsheets.v1`,  lib/rigsheet/storage.js)
//     • BHA               (`rsc.bha.v1`,        lib/bha/storage.js)
//     • Drill String      (`rsc.drillstring.v1`)
//     • Mud Program       (`rsc.mudPrograms.v1`,lib/mud/program.js)
//     • Engineering Defaults (`rsc.settings.v1.engineeringDefaults`)
//
//   This module is kept READ-ONLY for backward compatibility with users
//   whose data was created before the RCE shipped. Do NOT write to it
//   from new code. Removal is scheduled for V2.
//
// Storage model
// -----------------------------------------------------------------------------
// Each profile kind is stored as a single JSON array under a versioned key:
//   rsc.profiles.well.v1
//   rsc.profiles.bha.v1
//   rsc.profiles.casing.v1
//   rsc.profiles.mud.v1
//   rsc.profiles.pump.v1
//
// Each entry is { id, version, kind, label, createdAt, updatedAt, payload }.
//
// Versioning contract — required by user req #4 ("kill sheets are
// snapshot-locked"):
//   * Create        → version = 1
//   * Update        → version += 1, updatedAt = now()
//   * Clone         → new id, version = 1
//   * Delete        → removes ALL versions of that id (terminal)
//   * History       → all prior versions remain in storage for snapshot lookup
//                     by future kill sheets. Newest version is the "active"
//                     entry shown in list views.
//
// snapshot(id) -> returns the latest VERSION TUPLE { id, version } for the
// kill-sheet composer to lock against. getByVersion(id, version) -> returns
// the historical payload of that version (or null if it was deleted).

import {
  PROFILE_KINDS, VALIDATORS, EMPTY_FACTORIES,
} from "./schemas";
import { scoped } from "@/lib/accountScope";

const KEYS = {
  well:    "rsc.profiles.well.v1",
  bha:     "rsc.profiles.bha.v1",
  casing:  "rsc.profiles.casing.v1",
  mud:     "rsc.profiles.mud.v1",
  pump:    "rsc.profiles.pump.v1",
  opstate: "rsc.profiles.opstate.v1",
};

const _safeParse = (s, fallback) => {
  try { return JSON.parse(s) ?? fallback; } catch { return fallback; }
};

const _now = () => new Date().toISOString();

const _genId = () =>
  `prof_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

function _assertKind(kind) {
  if (!PROFILE_KINDS.includes(kind))
    throw new Error(`Unknown profile kind '${kind}'. Expected one of ${PROFILE_KINDS.join(", ")}.`);
}

function _readAll(kind) {
  _assertKind(kind);
  return _safeParse(scoped.getItem(KEYS[kind]), []);
}

function _writeAll(kind, rows) {
  _assertKind(kind);
  scoped.setItem(KEYS[kind], JSON.stringify(rows));
}

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------


// ---- Rig-aware lookup (P3 v6) ----
// Lightweight: each profile payload may carry a free-text `rig` tag.
// `latestForRig(kind, rig)` returns the first profile whose `payload.rig`
// matches (case-insensitive); falls back to the absolute latest of that
// kind when no match exists. Used by CreateKillSheetFromOpStateModal to
// pre-select rig-appropriate profiles.
export function latestForRig(kind, rig) {
  const all = listProfiles(kind);
  if (rig && typeof rig === "string" && rig.trim()) {
    const want = rig.trim().toLowerCase();
    const hit = all.find((r) => {
      const tag = r?.payload?.rig;
      return typeof tag === "string" && tag.trim().toLowerCase() === want;
    });
    if (hit) return hit;
  }
  return all[0] || null;
}

/** Returns only the LATEST version of every distinct profile id. */
export function listProfiles(kind) {
  const all = _readAll(kind);
  const latestById = new Map();
  for (const row of all) {
    const prev = latestById.get(row.id);
    if (!prev || row.version > prev.version) latestById.set(row.id, row);
  }
  // Newest createdAt first.
  return [...latestById.values()].sort(
    (a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""),
  );
}

/** Latest version of a single profile by id, or null. */
export function getLatest(kind, id) {
  const list = listProfiles(kind);
  return list.find((r) => r.id === id) || null;
}

/** A specific historical version of a profile, or null. */
export function getByVersion(kind, id, version) {
  const all = _readAll(kind);
  return all.find((r) => r.id === id && r.version === version) || null;
}

/** All versions of a single id, newest first. Useful for an audit panel. */
export function getHistory(kind, id) {
  const all = _readAll(kind);
  return all
    .filter((r) => r.id === id)
    .sort((a, b) => b.version - a.version);
}

/**
 * Create a new profile. Returns the persisted row.
 * payload is validated against the kind's validator and rejected on failure.
 */
export function createProfile(kind, payload) {
  _assertKind(kind);
  const result = VALIDATORS[kind](payload);
  if (!result.ok) {
    const err = new Error(`Invalid ${kind} profile: ${result.errors.join(" ")}`);
    err.validationErrors = result.errors;
    throw err;
  }
  const all = _readAll(kind);
  const row = {
    id: _genId(),
    version: 1,
    kind,
    label: (payload.label || "").trim(),
    createdAt: _now(),
    updatedAt: _now(),
    payload,
  };
  all.push(row);
  _writeAll(kind, all);
  return row;
}

/**
 * Update an existing profile by id. Creates a new version row; previous
 * versions remain intact for snapshot lookup by historical kill sheets.
 */
export function updateProfile(kind, id, payload) {
  _assertKind(kind);
  const result = VALIDATORS[kind](payload);
  if (!result.ok) {
    const err = new Error(`Invalid ${kind} profile: ${result.errors.join(" ")}`);
    err.validationErrors = result.errors;
    throw err;
  }
  const all = _readAll(kind);
  const versions = all.filter((r) => r.id === id);
  if (versions.length === 0) throw new Error(`Profile not found: ${kind}#${id}`);
  const nextVersion = versions.reduce((m, r) => Math.max(m, r.version), 0) + 1;
  const newRow = {
    id, version: nextVersion, kind,
    label: (payload.label || "").trim(),
    createdAt: versions[0].createdAt,
    updatedAt: _now(),
    payload,
  };
  all.push(newRow);
  _writeAll(kind, all);
  return newRow;
}

/**
 * Clone an existing profile. The new profile gets a fresh id and starts at
 * version 1. Useful for "save as" workflows in the editor.
 */
export function cloneProfile(kind, id, newLabel) {
  const latest = getLatest(kind, id);
  if (!latest) throw new Error(`Profile not found: ${kind}#${id}`);
  const payload = { ...latest.payload, label: (newLabel || latest.label) + " (copy)" };
  return createProfile(kind, payload);
}

/**
 * Delete a profile by id. Removes ALL versions of that id from storage.
 * Historical kill sheets referencing this id will fall back to their saved
 * snapshot copy (P3 composer handles this).
 */
export function deleteProfile(kind, id) {
  _assertKind(kind);
  const all = _readAll(kind);
  const filtered = all.filter((r) => r.id !== id);
  if (filtered.length === all.length) return false;
  _writeAll(kind, filtered);
  return true;
}

/**
 * Take a snapshot ref of a profile at its current latest version. Used by
 * P3 composer to lock a kill sheet to a specific profile state at the time
 * of composition.
 */
export function snapshotRef(kind, id) {
  const latest = getLatest(kind, id);
  if (!latest) return null;
  return { kind, id, version: latest.version };
}

/**
 * Test/diagnostic helper: wipe all profiles for a kind. Never called from UI.
 */
export function _clearAll(kind) {
  _assertKind(kind);
  scoped.removeItem(KEYS[kind]);
}

/**
 * Test/diagnostic helper: replace internal storage with a known list.
 * Used by unit tests to set up fixtures. Never called from UI.
 */
export function _seed(kind, rows) {
  _writeAll(kind, rows);
}

/** Storage keys are exported for diagnostic tooling. */
export const PROFILE_STORAGE_KEYS = KEYS;

/** Convenience: build a fresh empty-profile draft for the editor. */
export function newDraft(kind) {
  _assertKind(kind);
  return EMPTY_FACTORIES[kind]();
}
